/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { type ChangeEvent, useCallback, useMemo, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button.js';
import { ACCESS_LEVELS, RBAC_MODULES, type Role } from '../../lib/api/roles.js';
import { useCreateRole, useUpdateRoleAccess } from '../../lib/queries/roles.js';

const SELECT =
  'focus-ring rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white';

function initialLevels(role: Role | null): Record<string, number> {
  const out: Record<string, number> = {};
  for (const m of RBAC_MODULES) {
    out[m] = 0;
  }
  for (const a of role?.modules ?? []) {
    out[a.module] = a.accessLevel;
  }
  return out;
}

/**
 * Create / edit a role's module access matrix (Super Admin RBAC). Custom roles
 * are edited in place; a built-in (system) role is editable too but saves as a
 * per-clinic override — the change is scoped to this tenant only. The name is
 * fixed once a role exists (create-only).
 */
export function RoleEditor({
  role,
  onDone,
}: {
  readonly role: Role | null;
  readonly onDone: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const create = useCreateRole();
  const update = useUpdateRoleAccess();
  const isSystem = role?.isSystem ?? false;
  const [name, setName] = useState(role?.name ?? '');
  const [levels, setLevels] = useState<Record<string, number>>(() => initialLevels(role));

  const onName = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  }, []);
  const onLevel = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    const key = e.target.dataset.module ?? '';
    const value = Number(e.target.value);
    setLevels((prev) => ({ ...prev, [key]: value }));
  }, []);

  const modules = useMemo(
    () => RBAC_MODULES.map((m) => ({ module: m, accessLevel: levels[m] ?? 0 })),
    [levels],
  );
  const pending = create.isPending || update.isPending;
  const onSave = useCallback(() => {
    const active = modules.filter((m) => m.accessLevel > 0);
    if (role !== null) {
      update.mutate({ id: role.id, modules: active }, { onSuccess: onDone });
    } else {
      create.mutate({ name: name.trim(), modules: active }, { onSuccess: onDone });
    }
  }, [modules, role, update, create, name, onDone]);

  return (
    <div className="space-y-4">
      <div>
        <label
          htmlFor="role-name"
          className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300"
        >
          {t('rbac.roleName')}
        </label>
        <input
          id="role-name"
          value={name}
          onChange={onName}
          disabled={role !== null}
          placeholder={t('rbac.roleNamePlaceholder')}
          className="focus-ring w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
        />
      </div>
      {isSystem && (
        <p className="bg-[var(--accent)]/10 rounded-lg px-3 py-2 text-xs text-[var(--accent)]">
          {t('rbac.systemOverrideNote')}
        </p>
      )}
      <div className="max-h-[45vh] space-y-1 overflow-y-auto rounded-xl border border-slate-100 p-2 dark:border-slate-800">
        {RBAC_MODULES.map((m) => (
          <div
            key={m}
            className="flex items-center justify-between gap-3 rounded-lg px-2 py-1.5 hover:bg-slate-50 dark:hover:bg-slate-800/60"
          >
            <span className="text-sm text-slate-700 dark:text-slate-200">
              {t(`rbac.module.${m}`)}
            </span>
            <select
              data-module={m}
              value={levels[m] ?? 0}
              onChange={onLevel}
              aria-label={t(`rbac.module.${m}`)}
              className={SELECT}
            >
              {ACCESS_LEVELS.map((lvl) => (
                <option key={lvl} value={lvl}>
                  {t(`rbac.level.${String(lvl)}`)}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-3">
        <Button type="button" variant="outline" onClick={onDone}>
          {t('common.cancel')}
        </Button>
        <Button
          type="button"
          onClick={onSave}
          disabled={pending || (role === null && name.trim() === '')}
        >
          {role !== null ? t('rbac.saveAccess') : t('rbac.createRole')}
        </Button>
      </div>
    </div>
  );
}
